import { useQuery } from '@apollo/client'
import { useAuth0 } from '@auth0/auth0-react'
import { Alert, ButtonLink } from '@designsforhealth/dfh-react-components'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@dfh-corp-sites/ui'
import { Trash } from '@dfh-corp-sites/ui/icons'
import { Subscribe } from '@react-rxjs/core'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { GetProductsByUrlKeyDocument } from '../../../../graphql/magento'
import { useCustomerId } from '../../../../hooks/useCustomerId'
import useRetailPrices from '../../../../hooks/useRetailPrices'
import useSiteMetadata from '../../../../hooks/useSiteMetadata'
import { TokenContext } from '../../../../layouts/context'
import { cartItemDisplayName } from '../../../../lib/cart/item'
import {
  latestCartItemProduct$,
  setCartItemSku,
  useCartItemProduct,
} from '../../../../lib/cart/state/cart-items/cart-item-product'
import { useAutoUpdateErrantCartItems } from '../../../../lib/cart/state/cart-items/cart-item-update'
import useSanityImage from '../../../../lib/sanity/hooks/useSanityImage'
import { cartItemAndDesignFeePrice, formatAsCurrency } from '../../../../utils/priceTools'
import ItemWarningMessages from '../../../buy/cart/ItemWarningMessages'
import CartItemDescription from '../../../cart/CartItemDescription'
import ItemLoadingOverlay from '../../../cart/ItemLoadingOverlay'
import RequireLabelStatusPresent from '../../../cart/RequireLabelStatusPresent'
import StockAlertAndModal from '../../../cart/StockAlertAndModal'
import { CartItemProps } from '../../../cart/types'
import ErrorUpdatingCartMessage from '../../../ErrorUpdatingCartMessage'
import OptionalProductLink from '../../../OptionalProductLink'
import { takeIfConfigurableProduct } from '../../../shop/pdp/ItemContainer'
import { cn } from '../../../utils/cn'
import CartItemDescriptionModal from '../CartItemDescriptionModal'
import CartItemLabelInformation from '../CartItemLabelInformation'
import CartItemOrderTypeControl from '../CartItemOrderTypeControl'
import ItemImage from '../ItemImage'
import { CartItemQuantityControl } from './styled'

const CartItemRetailPrice: React.FC<{ sku: string }> = ({ sku }) => {
  const retailPrice = useCartItemProduct(sku).retailPrice
  if (!retailPrice) {
    return null
  }
  const amount = parseFloat(retailPrice.amount)
  if (Number.isNaN(amount)) {
    return null
  }
  return (
    <p className="text-xs my-0.5 text-gray-500 md:my-1 font-normal">
      {formatAsCurrency({ amount })} retail
    </p>
  )
}

const CartItemImage: React.FC<{ item: CartItemProps['item'] }> = ({ item }) => {
  const image = useCartItemProduct(item.product.sku ?? '').mainImage

  const sanityImageUrl = useSanityImage(
    { sourceJson: image?.json },
    {
      dpr: 3,
      fit: 'max',
      // set width only to avoid crop bug: https://github.com/sanity-io/image-url/issues/32
      width: 50,
    },
  )
  return (
    <ItemImage
      altText={item.product.name ?? ''}
      itemUrlKey={item.product.url_key}
      src={sanityImageUrl ?? ''}
      className="!min-h-[100px] !min-w-[100px] !max-h-[100px] !max-w-[100px] aspect-square bg-brand-navy-3"
    />
  )
}

const CartItemTagline: React.FC<{ sku: string }> = ({ sku }) => {
  const product = useCartItemProduct(sku ?? '')

  if (!product?.parentProduct?.tagline) {
    return null
  }
  return (
    <div className="text-sm font-normal line-clamp-1" title={product.parentProduct.tagline}>
      {product.parentProduct.tagline}
    </div>
  )
}

// Component to display variant options in a dropdown
const VariantSelector: React.FC<{
  currentSku: string
  disabled: boolean
  variants: Array<{ sku: string; label: string; disabled?: boolean; stockStatus?: string | null }>
  onVariantChange: (sku: string) => void
  attributeType: string
  className?: string
}> = ({ currentSku, disabled, variants, onVariantChange, attributeType, className }) => {
  // If the current SKU changes from outside, update the selected value
  useEffect(() => {
    // Validate that the currentSku is in the variants list
    if (currentSku && variants.some((v) => v.sku === currentSku)) {
      // The value is updated automatically by the controlled component
    }
  }, [currentSku, variants])

  // Determine if there are available variants to select
  const hasAvailableVariants = variants.some((v) => !v.disabled)
  const hasSingleOption = variants.length === 1

  return (
    <div className={cn('max-w-32', className)}>
      <Select
        value={currentSku}
        onValueChange={onVariantChange}
        disabled={disabled || !hasAvailableVariants}
      >
        <SelectTrigger className={cn('max-w-full')} aria-label={attributeType}>
          <SelectValue placeholder={attributeType} />
        </SelectTrigger>
        <SelectContent className="z-[12000]">
          {variants.map((variant) => (
            <SelectItem
              key={variant.sku}
              value={variant.sku}
              disabled={variant.disabled}
              className={cn(
                variant.disabled ? 'text-gray-400 cursor-not-allowed' : '',
                currentSku === variant.sku ? 'font-bold' : '',
                hasSingleOption && 'italic',
              )}
            >
              {variant.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

// Component to manage variant data and provide it to the VariantSelector
const CartItemVariantManager: React.FC<{
  sku: string
  currentSku: string
  disabled: boolean
  onVariantChange: (sku: string) => void
}> = ({ sku, currentSku, disabled, onVariantChange }) => {
  const product = useCartItemProduct(sku)
  const token = useContext(TokenContext)

  // Only query if we have a parent product URL key
  const { data, loading } = useQuery(GetProductsByUrlKeyDocument, {
    returnPartialData: true,
    context: { token },
    variables: { urlKey: product.parentProduct.urlKey },
    skip: !product.parentProduct.urlKey,
  })

  const configurableProduct = takeIfConfigurableProduct(data?.products?.items?.[0])

  // Extract variants from the configurable product
  const variants = useMemo(() => {
    if (!configurableProduct || !configurableProduct.variants) {
      return []
    }

    return (configurableProduct.variants || [])
      .filter(
        (variant): variant is NonNullable<typeof variant> =>
          variant !== null && variant !== undefined,
      )
      .filter((variant) => variant.attributes !== null && variant.attributes !== undefined)
  }, [configurableProduct])

  // Group variants by size and flavor
  const variantsByAttribute = useMemo(() => {
    // Get unique sizes and flavors
    const sizes = new Set<string>()
    const flavors = new Set<string>()

    // Map to track which variants are available for each size and flavor combination
    const skuMap = new Map<
      string,
      {
        sku: string
        stockStatus: string | null
        size: string | null
        flavor: string | null
        sizeLabel: string | null
        flavorLabel: string | null
      }
    >()

    // Track if the product has size and flavor attributes at all
    let hasSizeAttribute = false
    let hasFlavorAttribute = false

    variants.forEach((variant) => {
      const sizeAttr = variant.attributes?.find((attr) => attr?.code === 'size')
      const flavorAttr = variant.attributes?.find((attr) => attr?.code === 'flavor')

      if (sizeAttr) {
        hasSizeAttribute = true
        if (sizeAttr.value_index) {
          sizes.add(String(sizeAttr.value_index))
        }
      }

      if (flavorAttr) {
        hasFlavorAttribute = true
        if (flavorAttr.value_index) {
          flavors.add(String(flavorAttr.value_index))
        }
      }

      // Store the full variant info in our map
      if (variant.product?.sku) {
        skuMap.set(variant.product.sku, {
          sku: variant.product.sku,
          stockStatus: variant.product.stock_status || null,
          size: sizeAttr?.value_index ? String(sizeAttr.value_index) : null,
          flavor: flavorAttr?.value_index ? String(flavorAttr.value_index) : null,
          sizeLabel: sizeAttr?.label || null,
          flavorLabel: flavorAttr?.label || null,
        })
      }
    })

    // Create size options - for each size, find a variant with that size and the current flavor
    const currentVariant = skuMap.get(currentSku)
    const currentFlavor = currentVariant?.flavor
    const currentSize = currentVariant?.size

    let sizeOptions: Array<{
      sku: string
      label: string
      stockStatus: string | null
      disabled: boolean
    }> = []

    if (hasSizeAttribute) {
      sizeOptions = Array.from(sizes)
        .map((sizeId) => {
          // Find a variant with this size and the currently selected flavor (if possible)
          const matchingVariant = Array.from(skuMap.values()).find(
            (v) => v.size === sizeId && (currentFlavor ? v.flavor === currentFlavor : true),
          )

          if (!matchingVariant) return null

          return {
            sku: matchingVariant.sku,
            label: matchingVariant.sizeLabel || 'Unknown Size',
            stockStatus: matchingVariant.stockStatus,
            disabled: matchingVariant.stockStatus === 'OUT_OF_STOCK',
          }
        })
        .filter(Boolean) as Array<{
        sku: string
        label: string
        stockStatus: string | null
        disabled: boolean
      }>
    } else if (currentVariant) {
      // If no size attribute exists but we have a variant, create a placeholder option
      sizeOptions = [
        {
          sku: currentVariant.sku,
          label: 'Not Applicable',
          stockStatus: currentVariant.stockStatus,
          disabled: false,
        },
      ]
    }

    let flavorOptions: Array<{
      sku: string
      label: string
      stockStatus: string | null
      disabled: boolean
    }> = []

    if (hasFlavorAttribute) {
      flavorOptions = Array.from(flavors)
        .map((flavorId) => {
          // Find a variant with this flavor and the currently selected size (if possible)
          const matchingVariant = Array.from(skuMap.values()).find(
            (v) => v.flavor === flavorId && (currentSize ? v.size === currentSize : true),
          )

          if (!matchingVariant) return null

          return {
            sku: matchingVariant.sku,
            label: matchingVariant.flavorLabel || 'Unknown Flavor',
            stockStatus: matchingVariant.stockStatus,
            disabled: matchingVariant.stockStatus === 'OUT_OF_STOCK',
          }
        })
        .filter(Boolean) as Array<{
        sku: string
        label: string
        stockStatus: string | null
        disabled: boolean
      }>
    }

    return {
      sizeOptions,
      flavorOptions,
      // We consider having variants if either attribute has more than zero options
      hasMultipleOptions: sizeOptions.length > 0 || flavorOptions.length > 0,
      hasSizeOptions: sizeOptions.length > 0,
      hasFlavorOptions: flavorOptions.length > 0,
    }
  }, [variants, currentSku])

  // Check if the current variant is out of stock
  const currentVariantInfo = [
    ...variantsByAttribute.sizeOptions,
    ...variantsByAttribute.flavorOptions,
  ].find((v) => v.sku === currentSku)
  const isCurrentVariantOutOfStock = currentVariantInfo?.stockStatus === 'OUT_OF_STOCK'

  return (
    <div className="flex gap-2 my-2">
      <VariantSelector
        currentSku={currentSku}
        disabled={
          disabled ||
          loading ||
          isCurrentVariantOutOfStock ||
          variantsByAttribute.sizeOptions.length === 0
        }
        variants={
          variantsByAttribute.sizeOptions.length > 0
            ? variantsByAttribute.sizeOptions
            : [{ sku: currentSku, label: 'Not Available', disabled: true, stockStatus: null }]
        }
        onVariantChange={onVariantChange}
        attributeType="size"
      />

      {variantsByAttribute.hasFlavorOptions && (
        <VariantSelector
          currentSku={currentSku}
          disabled={
            disabled ||
            loading ||
            isCurrentVariantOutOfStock ||
            variantsByAttribute.flavorOptions.length === 0
          }
          variants={
            variantsByAttribute.flavorOptions.length > 0
              ? variantsByAttribute.flavorOptions
              : [{ sku: currentSku, label: 'Not Available', disabled: true, stockStatus: null }]
          }
          onVariantChange={onVariantChange}
          attributeType="flavor"
        />
      )}
    </div>
  )
}

const CartItem: React.FC<CartItemProps> = ({
  className,
  deleteCartItem,
  deleteError,
  item,
  labelDesignFeeItem,
  loading,
  updateCartItem,
  removeAndAddSimpleProducts,
  removeAndAddError,
  isConfirmation = false,
  shippingAddress,
  showWarning = false,
}) => {
  const { featureFlags } = useSiteMetadata() ?? {}
  const { shouldDisplayRetailPrice } = useRetailPrices()
  const { loading: autoUpdateItemsLoading, itemsPendingUpdate } = useAutoUpdateErrantCartItems()
  const isItemPendingUpdate = !!itemsPendingUpdate.find((cartItem) => cartItem.id === item.id)
  const itemIsLoading = loading || (autoUpdateItemsLoading && isItemPendingUpdate)
  const isProductLabelStatusEnabled = !!featureFlags?.productLabelStatus
  const [isChangingVariant, setIsChangingVariant] = useState(false)
  const authState = useAuth0()
  const customerId = useCustomerId()
  const isProvisionalUser = authState.isAuthenticated && !customerId
  const state = shippingAddress && shippingAddress.region && shippingAddress.region.code

  useEffect(() => {
    if (item.product.sku) {
      setCartItemSku(item.product.sku)
    }
  }, [item.product.sku])

  const handleReplaceItem = useCallback(
    (variantSku = '') => {
      if (
        removeAndAddSimpleProducts &&
        item.product.sku &&
        variantSku &&
        variantSku !== item.product.sku
      ) {
        // Only proceed if we have a different variant selected
        setIsChangingVariant(true)

        // Use the selected variant SKU
        removeAndAddSimpleProducts([{ sku: variantSku, quantity: item.quantity }]).finally(() => {
          setIsChangingVariant(false)
        })
      }
    },
    [removeAndAddSimpleProducts, item.product.sku, item.quantity],
  )

  // Determine if variant selection should be disabled
  const isVariantSelectionDisabled =
    itemIsLoading ||
    isChangingVariant ||
    !removeAndAddSimpleProducts ||
    item.product.stock_status === 'OUT_OF_STOCK'

  return (
    <article
      className={cn(
        className,
        'rounded-lg border border-brand-rule-grey p-2.5 w-full flex text-brand-navy bg-white relative mb-2.5',
        (loading || isChangingVariant) && 'opacity-50',
      )}
    >
      <form className={cn('w-full')} noValidate onSubmit={(e) => e.preventDefault()}>
        <header
          className={cn(
            'flex items-start w-full justify-between gap-2.5 border-b border-brand-rule-grey',
            isConfirmation && 'border-b-0',
          )}
        >
          <div className="flex gap-2.5">
            {item.product.sku && (
              <Subscribe source$={latestCartItemProduct$(item.product.sku)} fallback={null}>
                <CartItemImage item={item} />
              </Subscribe>
            )}
            <div className="max-w-52 md:max-w-64">
              <OptionalProductLink urlKey={item.product.url_key}>
                <h4 className="m-0 mb-1.5 leading-tight font-medium text-lg">
                  {cartItemDisplayName({ item })}
                </h4>
              </OptionalProductLink>

              {item.product.sku && (
                <Subscribe source$={latestCartItemProduct$(item.product.sku)} fallback={null}>
                  <CartItemTagline sku={item.product.sku} />
                </Subscribe>
              )}

              {item.product.sku && !isConfirmation && (
                <Subscribe source$={latestCartItemProduct$(item.product.sku)} fallback={null}>
                  <CartItemVariantManager
                    sku={item.product.sku}
                    currentSku={item.product.sku}
                    disabled={isVariantSelectionDisabled}
                    onVariantChange={handleReplaceItem}
                  />
                </Subscribe>
              )}
              {showWarning && (
                <ItemWarningMessages
                  messageCode={item.item_attributes?.prop65_message}
                  state={state}
                />
              )}
              <CartItemDescription item={item} modalComponent={CartItemDescriptionModal} />
            </div>
          </div>
          {!isConfirmation && (
            <div className="hidden md:flex gap-2 flex-col items-center w-20">
              <CartItemQuantityControl
                disabled={itemIsLoading}
                item={item}
                updateCartItem={updateCartItem}
                label=""
                className="!w-full"
              />
              <ButtonLink
                className="small-paragraph-text text-[#52616b] mb-0 leading-8 transition-colors hover:text-component-link self-end mt-3.5"
                disabled={itemIsLoading || isConfirmation}
                onClick={deleteCartItem}
                underline
              >
                <Trash className="w-6 h-7" />
              </ButtonLink>
            </div>
          )}
          {isConfirmation && (
            <span className="size-8 flex justify-center items-center border border-brand-rule-grey rounded-md text-xs font-semibold mr-2 mt-2">
              x{item.quantity}
            </span>
          )}
        </header>

        {isConfirmation && (
          <div className="flex justify-end self-end">
            <div
              className={`flex ml-auto mb-auto whitespace-nowrap items-center mt-3 md:mt-0 gap-1 text-black ${!isProductLabelStatusEnabled ? 'md:flex-row md:mb-unset' : ''}`}
            >
              {isProductLabelStatusEnabled ? (
                <>
                  <span className="text-sm">
                    <strong>{`Total: ${cartItemAndDesignFeePrice(item, labelDesignFeeItem)}`}</strong>
                  </span>
                  <span className="text-xs">
                    {`Unit price: ${formatAsCurrency({ amount: item.prices?.price.value || 0 })}`}
                  </span>
                  {!!labelDesignFeeItem && (
                    <span className="text-xs">
                      {`Design Fee: ${formatAsCurrency({
                        amount: labelDesignFeeItem.prices?.row_total.value || 0,
                      })}`}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span className="text-lg font-bold">
                    {formatAsCurrency({ amount: item.prices?.price.value || 0 })}
                  </span>
                  {shouldDisplayRetailPrice && <>|</>}
                  {item.product.sku && shouldDisplayRetailPrice && (
                    <Subscribe source$={latestCartItemProduct$(item.product.sku)} fallback={null}>
                      <CartItemRetailPrice sku={item.product.sku} />
                    </Subscribe>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {!isConfirmation && (
          <div
            className={`grid items-center ${!authState.isLoading && authState.isAuthenticated && !isProvisionalUser ? 'grid-cols-[auto_auto]' : 'flex justify-end'}`}
          >
            {/* If the user is not logged in or hasn't completed onboarding don't show autoship controls */}
            {!authState.isLoading && authState.isAuthenticated && !isProvisionalUser && (
              <>
                {isProductLabelStatusEnabled ? (
                  <Subscribe fallback={null}>
                    <RequireLabelStatusPresent sku={item.product.sku || ''}>
                      <CartItemOrderTypeControl
                        disabled={itemIsLoading}
                        item={item}
                        updateCartItem={updateCartItem}
                      />
                    </RequireLabelStatusPresent>
                  </Subscribe>
                ) : (
                  <CartItemOrderTypeControl
                    disabled={itemIsLoading}
                    item={item}
                    updateCartItem={updateCartItem}
                  />
                )}
              </>
            )}
            <div className="flex flex-col mt-3.5 self-start">
              <div className="flex gap-3.5 justify-center self-end md:hidden items-center">
                <CartItemQuantityControl
                  disabled={itemIsLoading}
                  item={item}
                  updateCartItem={updateCartItem}
                  label=""
                />

                <div className="flex gap-2 justify-end">
                  <ButtonLink
                    className="small-paragraph-text text-[#52616b] mb-0 leading-8 transition-colors hover:text-component-link"
                    disabled={itemIsLoading || isConfirmation}
                    onClick={deleteCartItem}
                    underline
                  >
                    <Trash className="w-6 h-7" />
                  </ButtonLink>
                </div>
              </div>

              <div
                className={`flex ml-auto mb-auto whitespace-nowrap items-center mt-3 md:mt-0 gap-1 text-black ${!isProductLabelStatusEnabled ? 'md:flex-row md:mb-unset' : ''}`}
              >
                {isProductLabelStatusEnabled ? (
                  <>
                    <span className="text-sm">
                      <strong>{`Total: ${cartItemAndDesignFeePrice(item, labelDesignFeeItem)}`}</strong>
                    </span>
                    <span className="text-xs">
                      {`Unit price: ${formatAsCurrency({ amount: item.prices?.price.value || 0 })}`}
                    </span>
                    {!!labelDesignFeeItem && (
                      <span className="text-xs">
                        {`Design Fee: ${formatAsCurrency({
                          amount: labelDesignFeeItem.prices?.row_total.value || 0,
                        })}`}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="text-lg font-bold">
                      {formatAsCurrency({ amount: item.prices?.price.value || 0 })}
                    </span>
                    {shouldDisplayRetailPrice && <>|</>}
                    {item.product.sku && shouldDisplayRetailPrice && (
                      <Subscribe source$={latestCartItemProduct$(item.product.sku)} fallback={null}>
                        <CartItemRetailPrice sku={item.product.sku} />
                      </Subscribe>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {isProductLabelStatusEnabled && (
          <Subscribe fallback={null}>
            <CartItemLabelInformation sku={item.product.sku || ''} labelItem={labelDesignFeeItem} />
          </Subscribe>
        )}
        {deleteError && (
          <Alert type="error" className="break-words">
            <ErrorUpdatingCartMessage />
          </Alert>
        )}
        {removeAndAddError && (
          <Alert type="error" className="mt-2 break-words">
            <div>Failed to change variant. {removeAndAddError.message}</div>
            <ErrorUpdatingCartMessage />
          </Alert>
        )}
        <StockAlertAndModal item={item} />
      </form>
      {(itemIsLoading || isChangingVariant) && <ItemLoadingOverlay />}
    </article>
  )
}

export default CartItem
